
import { useEffect, useState } from "react";
import { Navigate, Outlet, useLocation } from "react-router-dom";
import { onAuthStateChanged } from "firebase/auth";
import { doc, getDoc } from "firebase/firestore";

import { auth, db } from "../firebase";

const APPROVED_STATUSES = ["approved", "active"];

function LoadingScreen() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#FFF9F0] px-4">
      <div className="text-center">
        <div className="mx-auto mb-3 h-10 w-10 animate-spin rounded-full border-4 border-[#7A1830] border-t-transparent" />
        <p className="text-sm text-gray-600">
          Checking your account...
        </p>
      </div>
    </div>
  );
}

function VerificationError({ onRetry }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-[#FFF9F0] px-4">
      <div className="w-full max-w-md rounded-2xl border border-amber-200 bg-white p-6 text-center shadow-sm">
        <h1 className="text-xl font-semibold text-[#7A1830]">
          Account verification failed
        </h1>

        <p className="mt-3 text-sm leading-6 text-gray-600">
          We couldn't verify your account right now. Please check
          your internet connection and Firestore permissions, then
          try again.
        </p>

        <button
          type="button"
          onClick={onRetry}
          className="mt-5 rounded-lg bg-[#7A1830] px-5 py-3 text-sm font-semibold text-white transition hover:bg-[#5d1023]"
        >
          Try again
        </button>
      </div>
    </div>
  );
}

export default function ProtectedRoute({ allowedRoles = [] }) {
  const location = useLocation();

  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [verificationError, setVerificationError] = useState(false);
  const [retryKey, setRetryKey] = useState(0);

  useEffect(() => {
    let active = true;

    const unsubscribe = onAuthStateChanged(
      auth,
      async (currentUser) => {
        if (!active) return;

        setLoading(true);
        setVerificationError(false);
        setUser(currentUser);
        setProfile(null);

        if (!currentUser) {
          setLoading(false);
          return;
        }

        try {
          // Read the signed-in user's own Firestore profile.
          const profileSnapshot = await getDoc(
            doc(db, "users", currentUser.uid)
          );

          if (!active) return;

          if (profileSnapshot.exists()) {
            setProfile(profileSnapshot.data());
          } else {
            setProfile(null);
          }

          setLoading(false);
        } catch (error) {
          console.error(
            "Protected route verification error:",
            error
          );

          if (active) {
            setVerificationError(true);
            setLoading(false);
          }
        }
      }
    );

    return () => {
      active = false;
      unsubscribe();
    };
  }, [retryKey]);

  if (loading) {
    return <LoadingScreen />;
  }

  if (!user) {
    return (
      <Navigate
        to="/signin"
        replace
        state={{ from: location }}
      />
    );
  }

  if (verificationError) {
    return (
      <VerificationError
        onRetry={() => {
          setVerificationError(false);
          setLoading(true);
          setRetryKey((key) => key + 1);
        }}
      />
    );
  }

  // Admin access is checked using users/{uid}.role in Firestore.
  if (allowedRoles.includes("admin")) {
    if (profile?.role === "admin") {
      return <Outlet />;
    }

    return <Navigate to="/" replace />;
  }

  // Other protected routes require a valid matching role.
  if (!profile || !allowedRoles.includes(profile.role)) {
    return <Navigate to="/" replace />;
  }

  const status = String(profile.status || "")
    .trim()
    .toLowerCase();

  const applicationStatus = String(
    profile.applicationStatus || ""
  )
    .trim()
    .toLowerCase();

  // Reject applications that have been rejected.
  if (
    status === "rejected" ||
    applicationStatus === "rejected"
  ) {
    return <Navigate to="/signin" replace />;
  }

  // Pending reseller/shop-owner accounts cannot access dashboards.
  if (!APPROVED_STATUSES.includes(status)) {
    return <Navigate to="/signin" replace />;
  }

  return <Outlet />;
}
